import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { useMessagerieStore } from "@store/messagerieStore";
import { isGUEST } from "@helpers/accessHelpers";
import ComponentsScreen from "@screens/ComponentsScreen";
import LoginScreen from "@screens/auth/LoginScreen";
import AcceptInvitationScreen from "@screens/auth/AcceptInvitationScreen";
import SideBar from "./SideBar";
import { ErrorProvider } from "context/ErrorContext";
import { NavigationProvider } from "context/NavigationContext";
import { NotificationProvider } from "context/NotificationContext";
import SettingsScreen from "@screens/SettingsScreen";
import ProjectsScreen from "@screens/ProjectsScreen";
import ProjectScreen from "@screens/ProjectScreen";
import PartnersScreen from "@screens/PartnersScreen";
import BroadcastsScreen from "@screens/BroadcastsScreen";
import EmailsSentScreen from "@screens/EmailsSentScreen";
import HomeScreen from "@screens/HomeScreen";
import ReceptionScreen from "@screens/reception/ReceptionScreen";
import AtelierScreen from "@screens/atelier/AtelierScreen";
import SignScreen from "@screens/SignScreen";
import TrackingScreen from "@screens/TrackingScreen";
import VehiculesScreen from "@screens/VehiculesScreen";
import MessagerieScreen from "@screens/MessagerieScreen";
import DossiersScreen from "@screens/DossiersScreen";
import DossierDetailScreen from "@screens/DossierDetailScreen";
import OrdresScreen from "@screens/OrdresScreen";
import OrdreDetailScreen from "@screens/OrdreDetailScreen";
import MobileLoginScreen from "@screens/mobile/MobileLoginScreen";
import MobileOrdresScreen from "@screens/mobile/MobileOrdresScreen";
import MobileOrdreDetailScreen from "@screens/mobile/MobileOrdreDetailScreen";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

/** Redirects small screens to /m unless already on a mobile route */
function MobileRedirect() {
  const isMobile = useIsMobile();
  const location = useLocation();
  if (isMobile && !location.pathname.startsWith("/m")) {
    return <Navigate to="/m" replace />;
  }
  return null;
}

function MainLayout() {
  const initGlobal = useMessagerieStore((s) => s.initGlobal);
  const orgMembers = useDataStore((s) => s.orgMembers);

  useEffect(() => {
    initGlobal();
  }, [orgMembers]);

  return (
    <div className="flex flex-row overflow-x-hidden">
      <MobileRedirect />
      <SideBar />
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

export default function Navigation() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const userData = useDataStore((s) => s.userData);
  const isGuest = isGUEST(userData);

  if (loading) {
    return <div className="bg-neutral-50 h-screen w-screen" />; // Loading state
  }

  return (
    <div className="bg-neutral-50">
      <ErrorProvider>
        <NotificationProvider>
          <BrowserRouter>
            <NavigationProvider>
              <Routes>
                {/* Public routes — accessible without authentication */}
                <Route path="/sign/:token" element={<SignScreen />} />
                <Route path="/tracking/:id" element={<TrackingScreen />} />
                <Route path="/accept-invitation" element={<AcceptInvitationScreen />} />
                {user ? (
                  <>
                    {/* Mobile routes — no sidebar */}
                    <Route path="/m" element={<MobileOrdresScreen />} />
                    <Route path="/m/ordre/:id" element={<MobileOrdreDetailScreen />} />

                    <Route element={<MainLayout />}>
                      <Route
                        path="/"
                        element={<Navigate to="/home" replace />}
                      />
                      <Route path="/home" element={<HomeScreen />} />
                      <Route path="/reception" element={<ReceptionScreen />} />
                      <Route path="/atelier" element={<AtelierScreen />} />
                      <Route path="/dossiers" element={<DossiersScreen />} />
                      <Route path="/dossier/:id" element={<DossierDetailScreen />} />
                      <Route path="/ordres" element={<OrdresScreen />} />
                      <Route path="/ordre/:id" element={<OrdreDetailScreen />} />
                      <Route path="/vehicules" element={<VehiculesScreen />} />
                      <Route path="/messagerie" element={<MessagerieScreen />} />
                      {/* Legacy Hexaflow routes — kept for template reuse
                      <Route path="/projects" element={<ProjectsScreen />} />
                      <Route path="/project/:id" element={<ProjectScreen />} />
                      <Route
                        path="/partners"
                        element={isGuest ? <Navigate to="/projects" replace /> : <PartnersScreen />}
                      />
                      <Route
                        path="/broadcasts"
                        element={isGuest ? <Navigate to="/projects" replace /> : <BroadcastsScreen />}
                      />
                      <Route
                        path="/emails-sent"
                        element={isGuest ? <Navigate to="/projects" replace /> : <EmailsSentScreen />}
                      />
                      */}
                      <Route path="/settings" element={<SettingsScreen />} />
                      <Route
                        path="/components"
                        element={isGuest ? <Navigate to="/projects" replace /> : <ComponentsScreen />}
                      />
                      <Route
                        path="*"
                        element={<Navigate to="/home" replace />}
                      />
                    </Route>
                  </>
                ) : (
                  <>
                    <Route path="/m" element={<MobileLoginScreen />} />
                    <Route path="/m/*" element={<MobileLoginScreen />} />
                    <Route path="/login" element={<LoginScreen />} />
                    <Route
                      path="*"
                      element={<Navigate to="/login" replace />}
                    />
                  </>
                )}
              </Routes>
            </NavigationProvider>
          </BrowserRouter>
        </NotificationProvider>
      </ErrorProvider>
    </div>
  );
}
